import { gql } from "lib/apolloClient";

import {
  gradeFragment,
  staffFragment,
  studentFragment,
  userFragment,
  genericFolderFragment,
  subjectFragment,
  subjectGroupFragment,
  curriculumProgramFragment,
  academicYearFragment,
  projectGroupFragments,
} from "modules/CommonFragments";

export const genericTagFragment = {
  genericTag: gql`
    fragment basicGenericTagItem on GenericTag {
      id
      type
      label
    }
  `,
};

export const courseFragment = {
  courseFeed: gql`
    fragment courseFeedItem on Course {
      id
      title
      primaryGrade(filters: $courseGradeFilters) {
        id
        name
      }
      profileImageData {
        url
        icon
        color
      }
      grades(filters: $courseGradeFilters) {
        id
        name
        globalGrade {
          id
          uid
          name
          constants
        }
        displaySequence
      }
      calendar {
        id
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      academicYears {
        ...academicYearItem
      }
      subjects {
        id
        name
        type
      }
      subjectGroups {
        id
        name
      }
      projectGroups {
        ...basicProjectGroupDetails
      }
      isDemo
      isRemovedCourseForLoggedInUser
      isArchived
      genericTags {
        id
        label
        type
      }
      enabledModules(filters: $enabledModulesFilters)
      learningCourse {
        id
        title
        academicCourse {
          id
          label
        }
        gradingPeriods {
          edges {
            node {
              id
            }
          }
        }
        displayMetadata
      }
    }
    ${academicYearFragment.academicYear}
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${projectGroupFragments.basicProjectGroupDetails}
  `,
  courseDetails: gql`
    fragment courseDetailsItem on Course {
      id
      title
      isArchived
      unitPlans {
        totalCount
      }
      flatUnitPlans {
        totalCount
      }
      organization {
        id
      }
      collaborators: allCollaborators(filters: $collaboratorFilters) {
        edges {
          node {
            id
            profileImage
            firstName
            middleName
            type
            lastName
            email
          }
        }
      }
      students: allStudents {
        edges {
          node {
            id
            profileImage
            firstName
            middleName
            type
            lastName
            email
          }
        }
      }
      isDemo
      subjects {
        ...subjectWithSubjectGroupItem
        recommendedPhase {
          gradeId
          phaseId
        }
      }
      subjectGroups {
        ...subjectGroupBasicDetailsItem
        type
      }
      primaryGrade(filters: $courseGradeFilters) {
        id
        name
        yearGroup {
          id
          examSession {
            id
            session
          }
        }
      }
      grades(filters: $courseGradeFilters) {
        id
        name
        subjects {
          id
          name
          isArchived
          subjectGroup {
            id
            name
          }
        }
        yearGroup {
          id
          name
          examSession {
            id
            year
          }
        }
        displaySequence
        globalGrade {
          id
          uid
          name
          constants
        }
      }
      calendar {
        id
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      academicYears {
        ...academicYearItem
      }
      genericTags {
        ...basicGenericTagItem
      }
      subjectLevelMap {
        associatedLevels {
          ...basicGenericTagItem
        }
      }
      projectGroups(
        filters: {
          projectGroupSubTypes: [CORE, INTERNAL_ASSESSMENT, EXTERNAL_ASSESSMENT]
        }
      ) {
        ...basicProjectGroupDetails
      }
      isArchived
      tags {
        id
        key
        value
      }
      profileImageData {
        url
        icon
      }
      learningCourse {
        id
        title
        academicCourse {
          id
          label
        }
        displayMetadata
        isArchived
      }
    }
    ${academicYearFragment.academicYear}
    ${subjectFragment.subjectWithSubjectGroup}
    ${subjectGroupFragment.subjectGroupBasicDetails}
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${genericTagFragment.genericTag}
    ${projectGroupFragments.basicProjectGroupDetails}
  `,
  courseStudentsFolders: gql`
    fragment courseItem on Course {
      id
      title
      students {
        edges {
          node {
            ...studentItem
          }
        }
      }
      genericFolders(type: $folderType) {
        ...genericFolderItem
      }
      tags {
        id
        key
        value
      }
      subjects {
        id
        type
        name
      }
    }
    ${studentFragment.basicStudentDetails}
    ${genericFolderFragment.genericFolderBasicDetails}
  `,
  courseUnitPlanFeedCount: gql`
    fragment courseItem on Course {
      id
      title
      isArchived
      enabledModules(filters: $enabledModulesFilters)
      grades(filters: $courseGradeFilters) {
        ...gradeItem
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      calendar {
        id
      }
      isDemo
      academicYears {
        ...academicYearItem
      }
      subjectGroups {
        id
        name
      }
      projectGroups {
        ...basicProjectGroupDetails
      }
      profileImage
      isRemovedCourseForLoggedInUser
      tags {
        id
        key
        value
      }
      subjects {
        id
        name
      }
    }
    ${academicYearFragment.academicYear}
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${gradeFragment.gradeUnitDetails}
    ${projectGroupFragments.basicProjectGroupDetails}
  `,
  courseCollaborator: gql`
    fragment courseItem on Course {
      id
      collaborators: allCollaborators {
        totalCount
        edges {
          node {
            ...staffItem
          }
        }
      }
    }
    ${staffFragment.basicStaffDetails}
  `,
  StaffCourseStudents: gql`
    fragment courseItem on Course {
      id
      title
      allStudents(filters: { searchText: $searchText }) {
        totalCount
        edges {
          node {
            ...userItem
            familyMembers {
              ...userItem
            }
          }
        }
      }
    }
    ${userFragment.basicUserDetails}
  `,
  courseBasicDetails: gql`
    fragment courseItem on Course {
      id
      title
      profileImage
      grades(filters: $courseGradeFilters) {
        id
        name
      }
      allStudents @include(if: $areStudentsIncluded) {
        edges {
          node {
            ...studentItem
          }
        }
      }
      subjects {
        id
        type
        name
        metadata
      }
      isArchived
      enabledModules(filters: $enabledModulesFilters)
      aclControlParameters(filters: $aclControlParameterFilter) {
        id
        uid
        permission
        value
      }
    }
    ${studentFragment.basicStudentDetails}
  `,
  courseGenericFoldersDetailsFragment: gql`
    fragment courseItem on Course {
      id
      genericFolders(type: $folderType) {
        ...genericFolderItem
      }
    }
    ${genericFolderFragment.genericFolderDetails}
  `,
  courseGenericFoldersBasicDetailsFragment: gql`
    fragment courseItem on Course {
      id
      genericFolders(type: $folderType) {
        ...genericFolderItem
      }
    }
    ${genericFolderFragment.genericFolderBasicDetails}
  `,
  courseUnitPlansCountFragment: gql`
    fragment courseUniPlansItem on Course {
      unitPlans: flatUnitPlans {
        totalCount
      }
    }
  `,
  courseStudentFoldersCountFragment: gql`
    fragment courseStudentFoldersItem on Course {
      studentDriveV2(filters: {}) {
        resourceTypeBasedCount {
          totalCount
        }
      }
    }
  `,
  courseAssesmentEvaluationCountFragment: gql`
    fragment courseAssesmentEvaluationCountItem on Course {
      assessmentEvaluations {
        totalCount
      }
    }
  `,
  courseBookmarkDetailsFragment: gql`
    fragment courseBookmarkDetailsItem on Course {
      id
      isBookmark
    }
  `,
};

export const courseSubjectGroupFragment = {
  basicSubjectGroupDetails: gql`
    fragment courseSubjectGroupItem on Course {
      subjectGroups {
        id
        name
        type
      }
    }
  `,
};

export const courseSubjectFragment = {
  basicSubjectDetails: gql`
    fragment courseSubjectItem on Course {
      subjects {
        id
        type
        subjectGroup {
          id
          name
          constants
        }
      }
    }
  `,
};

export const courseCurriuclumProgramFragment = {
  basicCurriculumProgramDetails: gql`
    fragment courseCurriculumProgramItem on Course {
      curriculumProgram {
        id
        type
      }
    }
  `,
};
